import React, { useState } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Content,
  Flex,
  FlexItem,
  Label,
  Spinner,
  Title,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationTriangleIcon, PlusCircleIcon, SyncAltIcon } from '@patternfly/react-icons';
import { formatPrice } from '../../../utils/money';
import { formatDateTime } from '../../../utils/dateFormat';
import type { UseWalletReturn } from '../../../hooks/useWallet';
import type { WalletDeposit, WalletDepositStatus } from '../../../types/models/Wallet';
import { BalanceAmount, isNegativeMoney } from './paymentDisplay';
import TopUpModal from './TopUpModal';

interface WalletCardProps {
  wallet: UseWalletReturn;
}

const DEPOSIT_STATUS_COLOR: Record<WalletDepositStatus, 'grey' | 'blue' | 'orange' | 'green' | 'red'> = {
  created: 'grey',
  open: 'blue',
  pending: 'blue',
  authorized: 'orange',
  paid: 'green',
  failed: 'red',
  expired: 'red',
  canceled: 'red',
};

// `created` = the Mollie payment isn't confirmed to exist yet (a saved-card top-up whose create
// response was lost): the backend resolves it at Mollie on the next poll, and marks it failed after
// 15 minutes with no payment — so it is shown as still starting, not as an error.
const DEPOSIT_STATUS_TEXT: Record<WalletDepositStatus, string> = {
  created: 'Starting',
  open: 'Awaiting payment',
  pending: 'Pending',
  authorized: 'Authorized',
  paid: 'Paid',
  failed: 'Failed',
  expired: 'Expired',
  canceled: 'Canceled',
};

const DepositStatusLabel: React.FunctionComponent<{ status: WalletDepositStatus }> = ({ status }) => (
  <Label isCompact color={DEPOSIT_STATUS_COLOR[status] ?? 'grey'}>
    {DEPOSIT_STATUS_TEXT[status] ?? status}
  </Label>
);

const depositSourceText = (deposit: WalletDeposit): string => (deposit.source === 'card' ? 'Saved card' : 'Checkout');

/**
 * "Prepaid balance" card — the balance (red + warning when negative), the top-up bounds, the top-ups
 * still awaiting a Mollie outcome, and the "Top up" action. Data comes from the parent's `useWallet()`
 * so the tab shares one fetch between this card and the Top-up modal.
 */
const WalletCard: React.FunctionComponent<WalletCardProps> = ({ wallet: walletState }) => {
  const { wallet, pendingDeposits, defaultPaymentMethod, loading, error, refetch } = walletState;
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);

  const negative = wallet !== null && isNegativeMoney(wallet.balance);

  const renderBody = () => {
    if (loading && wallet === null) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading your prepaid balance" />
        </Bullseye>
      );
    }
    if (error && wallet === null) {
      return (
        <Alert
          variant="danger"
          isInline
          title="Unable to load your prepaid balance"
          actionLinks={
            <Button variant="link" isInline onClick={() => void refetch()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      );
    }
    if (wallet === null) {
      return null;
    }
    return (
      <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsMd' }}>
        <FlexItem>
          <Flex alignItems={{ default: 'alignItemsBaseline' }} spaceItems={{ default: 'spaceItemsSm' }}>
            <FlexItem>
              <Title headingLevel="h3" size="3xl">
                <BalanceAmount value={wallet.balance} />
              </Title>
            </FlexItem>
            <FlexItem>
              <Content component="small">{wallet.currency}</Content>
            </FlexItem>
            {negative && (
              <FlexItem>
                <Label color="orange" icon={<ExclamationTriangleIcon />}>
                  Negative balance
                </Label>
              </FlexItem>
            )}
          </Flex>
          <Content component="small">
            Top-ups between {formatPrice(wallet.deposit_min)} and {formatPrice(wallet.deposit_max)}. Purchases paid from
            the balance are invoiced as usual.
          </Content>
        </FlexItem>

        {negative && (
          <FlexItem>
            <Alert variant="warning" isInline title="Your balance is negative">
              A refund or chargeback was applied to a top-up that had already been spent. The shortfall is a receivable
              our team will settle with you; topping up brings the balance back above zero.
            </Alert>
          </FlexItem>
        )}

        {error && (
          <FlexItem>
            <Alert variant="warning" isInline title="The balance shown may be out of date">
              {error}
            </Alert>
          </FlexItem>
        )}

        <FlexItem>
          <Title headingLevel="h4" size="md">
            Pending top-ups
          </Title>
          {pendingDeposits.length === 0 ? (
            <Content component="p">No pending top-up.</Content>
          ) : (
            <Table aria-label="Pending top-ups" variant="compact" borders={false}>
              <Thead>
                <Tr>
                  <Th>Amount</Th>
                  <Th>Paid with</Th>
                  <Th>Status</Th>
                  <Th>Started</Th>
                </Tr>
              </Thead>
              <Tbody>
                {pendingDeposits.map((deposit) => (
                  <Tr key={deposit.id}>
                    <Td dataLabel="Amount">{formatPrice(deposit.amount)}</Td>
                    <Td dataLabel="Paid with">{depositSourceText(deposit)}</Td>
                    <Td dataLabel="Status">
                      <DepositStatusLabel status={deposit.status} />
                    </Td>
                    <Td dataLabel="Started">{formatDateTime(deposit.created_at)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </FlexItem>
      </Flex>
    );
  };

  return (
    <>
      <Card isFullHeight>
        <CardHeader
          actions={{
            actions: (
              <>
                <Button
                  variant="secondary"
                  icon={<SyncAltIcon />}
                  onClick={() => void refetch()}
                  isLoading={loading}
                  isDisabled={loading}
                  aria-label="Refresh prepaid balance"
                >
                  Refresh
                </Button>
                <Button
                  variant="primary"
                  icon={<PlusCircleIcon />}
                  onClick={() => setIsTopUpOpen(true)}
                  isDisabled={wallet === null}
                >
                  Top up
                </Button>
              </>
            ),
          }}
        >
          <CardTitle>Prepaid balance</CardTitle>
        </CardHeader>
        <CardBody>{renderBody()}</CardBody>
      </Card>
      <TopUpModal
        isOpen={isTopUpOpen}
        onClose={() => setIsTopUpOpen(false)}
        onCredited={() => void refetch()}
        wallet={wallet}
        defaultPaymentMethod={defaultPaymentMethod}
        walletLoading={loading}
        walletError={error}
      />
    </>
  );
};

export default WalletCard;
